// app/(user)/profile/page.tsx
import { useState, useEffect, useCallback } from "react";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import UserInfoDisplay from "../../../components/user/UserInfoDisplay";
import KycSection from "../../../components/user/KycSection";
import Button from "../../../components/ui/Button";
import {
  getUserProfile,
  submitKyc,
} from "../../../lib/apiClient";
import { UserProfile, KycFormData } from "../../../types";
import { getErrorMessage } from "../../../lib/utils";
import { useAuth } from "../../../context/AuthContext";
import toast from "react-hot-toast";

export default function UserProfilePage() {
  const { updateAuthUser } = useAuth();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isKycLoading, setIsKycLoading] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setKycError(null);
    try {
      const data = await getUserProfile();
      setProfile(data);
    } catch (err) {
      const message = getErrorMessage(err);
      console.error("Failed to fetch profile:", message);
      setError(message);
      toast.error(`Error loading profile: ${message}`, {
        id: "fetch-profile-error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSubmitKyc = async (formData: KycFormData) => {
    setIsKycLoading(true);
    setKycError(null);
    try {
      const fullKycPayload: KycFormData = {
        pan_number: formData.pan_number,
        aadhar_number: formData.aadhar_number,
        dob: formData.dob,
      };

      await submitKyc(fullKycPayload);

      toast.success("KYC details submitted successfully! Thank you.", {
        id: "kyc-submit-success",
      });

      updateAuthUser({ isKycSubmitted: true });
      await fetchProfile();
    } catch (err) {
      const message = getErrorMessage(err);
      setKycError(message);
      toast.error(`Failed to submit KYC: ${message}`, {
        id: "kyc-submit-error",
      });
    } finally {
      setIsKycLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 md:px-8 lg:px-12 py-6 md:py-8">
        {" "}
        {/* Add padding for gutter */}
        <PageTitle title="My Profile" />
        <div className="flex justify-center py-10">
          <LoadingIndicator text="Loading your profile..." />{" "}
          {/* Adjust path if necessary */}
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="px-4 md:px-8 lg:px-12 py-6 md:py-8">
        {" "}
        {/* Add padding for gutter */}
        <PageTitle title="My Profile" />
        <div className="py-10">
          <ErrorMessage
            title="Error Loading Profile"
            message={error || "Could not load profile data."}
          />
        </div>
        <div className="flex justify-center mt-4">
          <Button onClick={fetchProfile}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-primary to-dark-secondary/30">
      <div className="px-4 sm:px-6 md:px-8 lg:px-12 py-6 sm:py-8 md:py-12 space-y-6 sm:space-y-8 md:space-y-12">
        {/* Enhanced Page Header */}
        <div className="text-center md:text-left">
          <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start mb-6 sm:mb-4 space-y-4 sm:space-y-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-16 md:h-16 bg-gradient-to-br from-golden-accent to-golden-accent-hover rounded-full flex items-center justify-center shadow-lg sm:mr-4">
              <span className="text-2xl sm:text-3xl md:text-2xl font-bold text-text-on-accent">
                {profile.name ? profile.name.charAt(0).toUpperCase() : profile.phone?.charAt(0) || 'U'}
              </span>
            </div>
                         <div className="text-center sm:text-left min-w-0 flex-1">
               <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-text-primary mb-1 bg-gradient-to-r from-golden-accent to-golden-accent-hover bg-clip-text text-transparent break-words">
                 Welcome, {profile.name || 'User'}
               </h1>
               <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                 Manage your account details and verification status
               </p>
             </div>
          </div>
        </div>

                 {/* Enhanced Cards Layout */}
         <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
           {/* Main Profile Card - Full width */}
           <div className="xl:col-span-2">
             <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-dark-border/50 shadow-xl sm:shadow-2xl hover:shadow-golden-accent/10 transition-all duration-300">
               <UserInfoDisplay profile={profile} isadmin={false} />
             </div>
           </div>

           {/* KYC Card */}
           <div className="xl:col-span-2">
             <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-dark-border/50 shadow-xl sm:shadow-2xl hover:shadow-golden-accent/10 transition-all duration-300">
               <div className="p-4 sm:p-6">
                 <KycSection
                   isKycSubmitted={profile.isKycSubmitted}
                   kycSubmissionDate={profile.kycSubmittedAt}
                   onSubmit={handleSubmitKyc}
                   profile={profile}
                   isLoading={isKycLoading}
                   error={kycError}
                 />
               </div>
             </div>
           </div>

           {/* Discord/Slack profile linking is deprecated in Phase 2 workflow. */}
         </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-gradient-to-br from-dark-secondary/60 to-dark-tertiary/40 backdrop-blur-xl rounded-xl border border-dark-border/30 p-4 sm:p-6 text-center hover:border-golden-accent/30 transition-all duration-300">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${profile.isVerified ? 'bg-functional-success/20' : 'bg-functional-warning/20'} rounded-full flex items-center justify-center mx-auto mb-3`}>
              {profile.isVerified ? (
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-functional-success" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-functional-warning" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-1">Verification</h3>
            <p className={`text-sm sm:text-base font-medium ${profile.isVerified ? 'text-functional-success' : 'text-functional-warning'}`}>
              {profile.isVerified ? 'Verified Account' : 'Pending Verification'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-dark-secondary/60 to-dark-tertiary/40 backdrop-blur-xl rounded-xl border border-dark-border/30 p-4 sm:p-6 text-center hover:border-golden-accent/30 transition-all duration-300">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${profile.isKycSubmitted ? 'bg-functional-success/20' : 'bg-functional-warning/20'} rounded-full flex items-center justify-center mx-auto mb-3`}>
              {profile.isKycSubmitted ? (
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-functional-success" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-functional-warning" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-1">KYC Status</h3>
            <p className={`text-sm sm:text-base font-medium ${profile.isKycSubmitted ? 'text-functional-success' : 'text-functional-warning'}`}>
              {profile.isKycSubmitted ? 'KYC Completed' : 'KYC Pending'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-dark-secondary/60 to-dark-tertiary/40 backdrop-blur-xl rounded-xl border border-dark-border/30 p-4 sm:p-6 text-center hover:border-golden-accent/30 transition-all duration-300">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-functional-info/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-functional-info" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-1">Member Since</h3>
            <p className="text-sm sm:text-base text-text-secondary font-medium">{new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
